import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MAX_HISTORY_PARAMETERS } from '../../services/measurementService';

/**
 * URL state of the "Verlauf" view (#1653), so a comparison can be reloaded and
 * shared: `?view=history&params=nitrate,phosphate,alkalinity&combined=1`.
 * Without `params` the view selects one parameter by default (as before), so
 * existing links keep working.
 */
export const PARAMS_KEY = 'params';
export const COMBINED_KEY = 'combined';

/** 1–64 characters, no control character and no comma (the separator) — the API's rule. */
function isParameterId(id: string): boolean {
  if (id.length < 1 || id.length > 64) return false;
  for (let i = 0; i < id.length; i += 1) {
    const code = id.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || code === 0x2c) return false;
  }
  return true;
}

/**
 * The parameter ids of a `params` value: trimmed, de-duplicated, 1–3 ids. A
 * malformed value (empty token, control characters, more than 3 ids) is
 * ignored as a whole → null, i.e. the default selection — the same rule the
 * API applies, so the view never sends a request it would reject.
 */
export function parseParamsParam(raw: string | null): string[] | null {
  if (raw === null) return null;
  const ids: string[] = [];
  for (const token of raw.split(',')) {
    const id = token.trim();
    if (!isParameterId(id)) return null;
    if (!ids.includes(id)) ids.push(id);
  }
  return ids.length > 0 && ids.length <= MAX_HISTORY_PARAMETERS ? ids : null;
}

export interface HistoryUrlState {
  /** Parameters from the URL, or null for the default selection. */
  urlParameters: string[] | null;
  /** "Gemeinsam darstellen" requested. */
  combined: boolean;
  setParameters: (ids: readonly string[]) => void;
  setCombined: (combined: boolean) => void;
  /** Drops `params` (e.g. an id the API does not know) → back to the default selection. */
  clearParameters: () => void;
}

export function useHistoryUrlState(): HistoryUrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawParams = searchParams.get(PARAMS_KEY);
  const urlParameters = useMemo(() => parseParamsParam(rawParams), [rawParams]);
  const combined = searchParams.get(COMBINED_KEY) === '1';

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setParameters = useCallback(
    (ids: readonly string[]) =>
      update((p) => {
        if (ids.length > 0) p.set(PARAMS_KEY, ids.slice(0, MAX_HISTORY_PARAMETERS).join(','));
        else p.delete(PARAMS_KEY);
      }),
    [update],
  );
  const setCombined = useCallback(
    (on: boolean) =>
      update((p) => {
        if (on) p.set(COMBINED_KEY, '1');
        else p.delete(COMBINED_KEY);
      }),
    [update],
  );
  const clearParameters = useCallback(() => update((p) => p.delete(PARAMS_KEY)), [update]);

  return { urlParameters, combined, setParameters, setCombined, clearParameters };
}
